import type { TFunction } from 'i18next';
import type { ZodValidationIssue, ApiValidationDetails } from '@trading/shared';

/**
 * Translates a single structured Zod validation issue to a human-readable string.
 * The `t` function must come from the `common` namespace.
 */
export function translateZodIssue(issue: ZodValidationIssue, t: TFunction): string {
  switch (issue.code) {
    case 'too_small': {
      const { minimum, type, inclusive } = issue as Extract<ZodValidationIssue, { code: 'too_small' }>;
      if (type === 'string') {
        return minimum === 1
          ? t('validation.required')
          : t('validation.minLength', { count: minimum });
      }
      if (type === 'number') {
        if (!inclusive && minimum === 0) return t('validation.positive');
        if (!inclusive) return t('validation.greaterThan', { min: minimum });
        return t('validation.minValue', { min: minimum });
      }
      if (type === 'array') {
        return t('validation.minItems', { count: minimum });
      }
      return t('validation.tooSmall');
    }
    case 'too_big': {
      const { maximum, type, inclusive } = issue as Extract<ZodValidationIssue, { code: 'too_big' }>;
      if (type === 'string') {
        return t('validation.maxLength', { count: maximum });
      }
      if (type === 'number') {
        if (!inclusive) return t('validation.lessThan', { max: maximum });
        return t('validation.maxValue', { max: maximum });
      }
      if (type === 'array') {
        return t('validation.maxItems', { count: maximum });
      }
      return t('validation.tooBig');
    }
    case 'invalid_string': {
      const { validation } = issue as Extract<ZodValidationIssue, { code: 'invalid_string' }>;
      if (validation === 'email') return t('validation.invalidEmail');
      if (validation === 'url') return t('validation.invalidUrl');
      if (validation === 'uuid') return t('validation.invalidUuid');
      if (validation === 'datetime') return t('validation.invalidDatetime');
      return t('validation.invalidFormat');
    }
    case 'invalid_type':
      return t('validation.invalidType');
    case 'invalid_enum_value':
      return t('validation.invalidOption');
    case 'custom': {
      // Custom refinement messages are i18n keys prefixed with 'validation.'
      const raw = (issue as { code: string; message?: string }).message;
      if (raw && raw.startsWith('validation.')) return t(raw);
      return t('validation.invalid');
    }
    default:
      return t('validation.invalid');
  }
}

/**
 * Translates all field-level validation issues from an API error response.
 * Returns a map of field path → translated message.
 */
export function translateApiValidationErrors(
  details: ApiValidationDetails,
  t: TFunction
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(details).map(([field, issue]) => [field, translateZodIssue(issue, t)])
  );
}
